// Slice de notificações — estado global app-wide de mensagens não lidas por conversa,
// usado pelos badges: bolinha no Sidebar "Conversas" (nº de CONVERSAS com não-lidas)
// e bolinha em cada linha da lista (nº de MENSAGENS não lidas daquela conversa).
//
// Fonte da verdade é o backend (Conversation.unreadCount, compartilhado entre
// atendentes); aqui o cliente espelha: seta o valor absoluto vindo de `message.received`
// e zera em `conversation.read` (sync entre atendentes) ou ao abrir a conversa.
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { conversationSelected } from '@/features/inbox';

export interface NotificationsState {
  /** conversationId → nº de mensagens do cliente não lidas. Sem a chave = 0. */
  unreadByConversation: Record<string, number>;
}

const initialState: NotificationsState = {
  unreadByConversation: {},
};

/** Aplica um valor absoluto: <=0 remove a chave (mantém o mapa enxuto). */
function setCount(state: NotificationsState, conversationId: string, count: number): void {
  if (count > 0) {
    state.unreadByConversation[conversationId] = count;
  } else {
    delete state.unreadByConversation[conversationId];
  }
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /** Espelha o unreadCount autoritativo do backend (message.received / conversation.read). */
    conversationUnreadSet(
      state,
      action: PayloadAction<{ conversationId: string; count: number }>,
    ) {
      setCount(state, action.payload.conversationId, action.payload.count);
    },
    /** Hidrata em lote no boot (GET /conversations/unread-summary). Substitui o estado. */
    unreadSeeded(
      state,
      action: PayloadAction<{ conversationId: string; unreadCount: number }[]>,
    ) {
      state.unreadByConversation = {};
      for (const { conversationId, unreadCount } of action.payload) {
        setCount(state, conversationId, unreadCount);
      }
    },
    notificationsCleared(state) {
      state.unreadByConversation = {};
    },
  },
  extraReducers: (builder) => {
    // Abrir uma conversa "lê" as mensagens dela → zera localmente (o markRead no
    // backend confirma e propaga `conversation.read` pros outros atendentes).
    builder.addCase(conversationSelected, (state, action) => {
      const { id } = action.payload;
      if (id) delete state.unreadByConversation[id];
    });
  },
});

export const { conversationUnreadSet, unreadSeeded, notificationsCleared } =
  notificationsSlice.actions;

export const notificationsReducer = notificationsSlice.reducer;

// ---------- Selectors ----------
type Root = { notifications: NotificationsState };

/** Sidebar: nº de CONVERSAS com mensagem nova (cada conversa conta 1). */
export const selectUnreadConversationCount = (s: Root): number =>
  Object.keys(s.notifications.unreadByConversation).length;

/** Mapa completo conversationId → nº de mensagens não lidas (linhas da lista). */
export const selectUnreadByConversation = (s: Root): Record<string, number> =>
  s.notifications.unreadByConversation;

/** Linha da lista: nº de mensagens não lidas de uma conversa específica. */
export const selectUnreadCountFor =
  (conversationId: string) =>
  (s: Root): number =>
    s.notifications.unreadByConversation[conversationId] ?? 0;
